import { NgTemplateOutlet } from "@angular/common";
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { ColourUtilities } from "@common/lib/utilities/colour-utilities";
import { IGroupedData } from "@common/lib/utilities/grouped-data.interface";
import { AdaptButtonModule } from "@common/ux/button/button.module";
import { AdaptTooltipModule } from "@common/ux/tooltip.directive/tooltip.module";
import { ValueChangedEvent } from "devextreme/ui/color_box";
import { DxColorBoxComponent } from "devextreme-angular";
import { DxoDropDownOptionsComponent } from "devextreme-angular/ui/nested";
import { CollapseDirective } from "ngx-bootstrap/collapse";

export type IColourPickerPresetGroups = IGroupedData<string, string>[];

const PRESET_SETS = ["light", "normal", "dark"] as const;
export type IColourPresetSet = typeof PRESET_SETS[number];

const COLOUR_NAMES = [
    "burgundy", "clementine", "future-lemon", "new-day-yellow",
    "wise-lime", "open-blue", "calm-violet",
];

const WEIGHTS: Record<IColourPresetSet, number[]> = {
    light: [25, 50, 100, 200],
    normal: [300, 400, 500, 600],
    dark: [700, 800, 900, 1000],
};

const LIGHT_PRESETS = [
    "#f1e7f8", "#eae8fd", "#e5edfc", "#e5f3f3",
    "#e7f4eb", "#ebf6e8", "#f2f7e0", "#fdf3e0",
    "#fdeee0", "#fce6e6", "#ececec", "#e0e0e0",
];

const NORMAL_PRESETS = [
    "#a061d1", "#7166f0", "#5484ed", "#50b1b1",
    "#5eb57d", "#78c464", "#a6cc33", "#f2b033",
    "#f28b33", "#ed5555", "#7f7f7f", "#333333",
];

const DARK_PRESETS = [
    "#704492", "#4f47a8", "#3b5ca6", "#387c7c",
    "#427f58", "#548946", "#748f24", "#a97b24",
    "#a96124", "#a63c3c", "#595959", "#242424",
];

const PRESETS_BY_SET: Record<IColourPresetSet, string[]> = {
    light: LIGHT_PRESETS,
    normal: NORMAL_PRESETS,
    dark: DARK_PRESETS,
};

@Component({
    selector: "adapt-colour-picker",
    templateUrl: "./colour-picker.component.html",
    styleUrl: "./colour-picker.component.css",
    imports: [
        AdaptButtonModule,
        AdaptTooltipModule,
        CollapseDirective,
        DxColorBoxComponent,
        DxoDropDownOptionsComponent,
        NgTemplateOutlet,
    ],
})
export class ColourPickerComponent implements OnChanges {
    @Input() public value?: string;
    @Output() public valueChange = new EventEmitter<string>();
    @Input() public disabled = false;

    @Input() public presetGroups?: IColourPickerPresetGroups;
    @Input() public expandedPresetsCollapsed = true;
    @Output() public expandedPresetsCollapsedChange = new EventEmitter<boolean>();

    @Input() public presetSet: IColourPresetSet = "normal";
    private presetSetIndex = 1;

    public presets: string[] = NORMAL_PRESETS;
    public selectedPreset?: string;
    public expandedPresets?: string[];

    public get canGoLighter() {
        return this.presetSetIndex > 0;
    }

    public get canGoDarker() {
        return this.presetSetIndex < PRESET_SETS.length - 1;
    }

    public constructor(private elementRef: ElementRef) {}

    public ngOnChanges() {
        this.updatePresetSet(PRESET_SETS.indexOf(this.presetSet));
    }

    public onColorChange(e: ValueChangedEvent) {
        if (e.value !== this.value) {
            this.selectedPreset = undefined;
            this.updateValue(e.value);
        }

        this.updateSelectedPreset();
    }

    public selectPreset(colour?: string) {
        this.selectedPreset = colour;
        const resolvedColour = ColourUtilities.resolveColour(colour ?? "", this.elementRef.nativeElement);
        this.updateValue(resolvedColour);
    }

    public goLighter() {
        this.updatePresetSet(this.presetSetIndex - 1);
    }

    public goDarker() {
        this.updatePresetSet(this.presetSetIndex + 1);
    }

    private updateValue(colour?: string) {
        this.value = colour;
        this.valueChange.emit(colour);
        this.updateSelectedPreset();
    }

    private updateSelectedPreset() {
        if (this.value) {
            if (this.selectedPreset &&
                ColourUtilities.resolveColour(this.selectedPreset, this.elementRef.nativeElement) === this.value) {
                return;
            }
            this.selectedPreset = this.presets
                .concat(this.expandedPresets ?? [])
                .concat(this.presetGroups?.flatMap((g) => g.items) ?? [])
                .find((p) => ColourUtilities.resolveColour(p, this.elementRef.nativeElement) === this.value);
        } else {
            this.selectedPreset = undefined;
        }
    }

    private updatePresetSet(presetSetIndex: number) {
        this.presetSetIndex = presetSetIndex;
        this.presetSet = PRESET_SETS[this.presetSetIndex];
        this.expandedPresets = this.generateExpandedPresets();
        this.presets = PRESETS_BY_SET[this.presetSet];
        this.updateSelectedPreset();
    }

    private generateExpandedPresets() {
        const weights = WEIGHTS[this.presetSet];
        return COLOUR_NAMES.flatMap((name) =>
            weights.map((w) => `var(--adapt-${name}-${w})`),
        );
    }
}
